import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import DateSearch from "../components/DateSearch";
import Sidebar from "../components/Sidebar";
import TableHeaderFilter from "../components/TableHeaderfilter";
import { Bars3Icon } from "@heroicons/react/24/outline";

/* ── Helpers ───────────────────────────────────────────── */

const calcDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;
    const [h1, m1] = checkIn.split(":").map(Number);
    const [h2, m2] = checkOut.split(":").map(Number);
    const diff = h2 * 60 + m2 - (h1 * 60 + m1);
    if (diff <= 0) return null;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
};

const getDayLabel = (dateStr) => {
    if (!dateStr) return "Older";
    const today = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor(
        (today.setHours(0, 0, 0, 0) - date.setHours(0, 0, 0, 0)) / 86400000
    );
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff <= 7) return "Last 7 Days";
    return "Older";
};

const DAY_LABEL_ORDER = ["Today", "Yesterday", "Last 7 Days", "Older"];

const DAY_LABEL_STYLES = {
    Today: {
        pill: "bg-emerald-50 text-emerald-700 border border-emerald-200",
        dot: "bg-emerald-500",
        separator: "bg-emerald-100 border-l-4 border-emerald-400 text-emerald-800",
    },
    Yesterday: {
        pill: "bg-amber-50 text-amber-700 border border-amber-200",
        dot: "bg-amber-500",
        separator: "bg-amber-50 border-l-4 border-amber-400 text-amber-800",
    },
    "Last 7 Days": {
        pill: "bg-blue-50 text-blue-700 border border-blue-200",
        dot: "bg-blue-500",
        separator: "bg-blue-50 border-l-4 border-blue-400 text-blue-800",
    },
    Older: {
        pill: "bg-slate-100 text-slate-600 border border-slate-200",
        dot: "bg-slate-400",
        separator: "bg-slate-50 border-l-4 border-slate-300 text-slate-700",
    },
};

/* ── Badge ─────────────────────────────────────────────── */
const Badge = ({ text }) => (
    <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full text-xs font-semibold capitalize">
        {text}
    </span>
);

/* ── StatCard ──────────────────────────────────────────── */
const StatCard = ({ label, value, colorClass, dotClass }) => (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorClass}`}>
        <span className={`w-2 h-2 rounded-full ${dotClass}`} />
        {label} <span className="font-extrabold">{value}</span>
    </div>
);

/* ═════════════════════════════════════════════════════════ */

export default function AdminDailyReport() {
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    /* ── Filters ───────────────────────────────────────── */
    const [employeeFilter, setEmployeeFilter] = useState([]);
    const [visitTypeFilter, setVisitTypeFilter] = useState([]);
    const [tallyFilter, setTallyFilter] = useState([]);
    const [clientTypeFilter, setClientTypeFilter] = useState([]);
    const [productFilter, setProductFilter] = useState([]);
    const [firmFilter, setFirmFilter] = useState([]);
    const [contactPersonFilter, setContactPersonFilter] = useState([]);

    /* ── Fetch ─────────────────────────────────────────── */
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${SERVER_URL}/Rahul/getDailyRoutine.php`, {
                params: { all: true },
            });
            if (res.data.success) {
                setRecords(res.data.data);
                setFilteredRecords(res.data.data);
            }
        } catch (err) {
            console.error("Fetch failed", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    /* ── Date Filter ───────────────────────────────────── */
    const handleDateSearch = (start, end, active) => {
        if (!active) { setFilteredRecords(records); return; }
        const filtered = records.filter((r) => {
            const d = new Date(r.routine_date);
            return d >= new Date(start) && d <= new Date(end);
        });
        setFilteredRecords(filtered);
    };

    /* ── Column Filters ────────────────────────────────── */
    const finalFilteredRecords = useMemo(() => {
        return filteredRecords.filter((row) => {
            const employeeOk = !employeeFilter.length || employeeFilter.includes(row.employee_name);
            const visitTypeOk = !visitTypeFilter.length || visitTypeFilter.includes(row.visit_type);
            const tallyOk = !tallyFilter.length || tallyFilter.includes(row.tally_serial_number);
            const clientTypeOk = !clientTypeFilter.length || clientTypeFilter.includes(row.client_type);
            const productOk = !productFilter.length || productFilter.includes(row.product);
            const firmOk = !firmFilter.length || firmFilter.includes(row.customer_name);
            const contactOk = !contactPersonFilter.length || contactPersonFilter.includes(row.contact_person);
            return employeeOk && visitTypeOk && tallyOk && clientTypeOk && productOk && firmOk && contactOk;
        });
    }, [filteredRecords, employeeFilter, visitTypeFilter, tallyFilter, clientTypeFilter, productFilter, firmFilter, contactPersonFilter]);

    /* ── Options ───────────────────────────────────────── */
    const createOptions = (key) =>
        [...new Set(records.map((r) => r[key]).filter(Boolean))].map((item) => ({
            label: item, value: item,
        }));

    const employeeOptions = createOptions("employee_name");
    const visitTypeOptions = createOptions("visit_type");
    const tallyOptions = createOptions("tally_serial_number");
    const clientTypeOptions = createOptions("client_type");
    const productOptions = createOptions("product");
    const firmOptions = createOptions("customer_name");
    const contactPersonOptions = createOptions("contact_person");

    /* ── Summary stats ─────────────────────────────────── */
    const stats = useMemo(() => {
        const today = finalFilteredRecords.filter(r => getDayLabel(r.routine_date) === "Today").length;
        const yesterday = finalFilteredRecords.filter(r => getDayLabel(r.routine_date) === "Yesterday").length;
        const last7 = finalFilteredRecords.filter(r => getDayLabel(r.routine_date) === "Last 7 Days").length;
        const older = finalFilteredRecords.filter(r => getDayLabel(r.routine_date) === "Older").length;
        return { today, yesterday, last7, older };
    }, [finalFilteredRecords]);

    /* ── Sort + annotate with day label ────────────────── */
    const sortedAnnotated = useMemo(() => {
        return [...finalFilteredRecords]
            .sort((a, b) => new Date(b.routine_date) - new Date(a.routine_date))
            .map(row => ({ ...row, _dayLabel: getDayLabel(row.routine_date) }));
    }, [finalFilteredRecords]);

    /* ── Build rows with separator injection ───────────── */
    const tableRows = useMemo(() => {
        const rows = [];
        let lastLabel = null;
        sortedAnnotated.forEach(row => {
            if (row._dayLabel !== lastLabel) {
                rows.push({ _isSeparator: true, _label: row._dayLabel, _count: sortedAnnotated.filter(r => r._dayLabel === row._dayLabel).length });
                lastLabel = row._dayLabel;
            }
            rows.push(row);
        });
        return rows;
    }, [sortedAnnotated]);

    /* ── UI ────────────────────────────────────────────── */
    return (
        <>
            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>

                {/* Top accent line */}
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50"
                    style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

                {/* ── STICKY HEADER ── */}
                <div className="sticky top-0 z-20 border-b border-slate-200"
                    style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                            >
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">
                                    Daily Reports
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="relative z-10 flex flex-col gap-4 p-5">

                    {/* ── FILTER BAR ── */}
                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap w-full">

                            {/* Summary stat pills */}
                            <div className="flex flex-wrap gap-2 flex-shrink-0">
                                <StatCard label="Today" value={stats.today}
                                    colorClass="bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    dotClass="bg-emerald-500" />
                                <StatCard label="Yesterday" value={stats.yesterday}
                                    colorClass="bg-amber-50 text-amber-700 border border-amber-200"
                                    dotClass="bg-amber-500" />
                                <StatCard label="Last 7 Days" value={stats.last7}
                                    colorClass="bg-blue-50 text-blue-700 border border-blue-200"
                                    dotClass="bg-blue-500" />
                                <StatCard label="Older" value={stats.older}
                                    colorClass="bg-slate-100 text-slate-600 border border-slate-200"
                                    dotClass="bg-slate-400" />
                            </div>

                            {/* Right: DateSearch */}
                            <div className="flex items-center gap-3 ml-auto">
                                <DateSearch onSearch={handleDateSearch} />
                            </div>
                        </div>
                    </div>

                    {/* ── TABLE ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200"
                        style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
                            <table className="min-w-[1600px] w-full text-xs">

                                {/* HEADER */}
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        <th className="p-3 text-left border-b border-slate-200 min-w-[40px]">
                                            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">#</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Employee" options={employeeOptions} selectedValues={employeeFilter} onChange={setEmployeeFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Date</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Visit Type" options={visitTypeOptions} selectedValues={visitTypeFilter} onChange={setVisitTypeFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Client Type" options={clientTypeOptions} selectedValues={clientTypeFilter} onChange={setClientTypeFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Purpose</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Product" options={productOptions} selectedValues={productFilter} onChange={setProductFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Firm Name" options={firmOptions} selectedValues={firmFilter} onChange={setFirmFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Address</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Contact Person" options={contactPersonOptions} selectedValues={contactPersonFilter} onChange={setContactPersonFilter} />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Contact No.</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">GPS Location</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <TableHeaderFilter label="Tally S/N" options={tallyOptions} selectedValues={tallyFilter} onChange={setTallyFilter} />
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Duration</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Remarks</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Referred By</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Ref. Contact</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Travel From → To</span>
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Travel Mode</span>
                                        </th>
                                        <th className="p-3 text-right border-b border-slate-200">
                                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Expense (₹)</span>
                                        </th>
                                    </tr>
                                </thead>

                                {/* BODY */}
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={20} className="p-10 text-center text-slate-400 text-sm">
                                                <span className="inline-block animate-pulse">Loading…</span>
                                            </td>
                                        </tr>
                                    ) : tableRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={20} className="p-10 text-center text-slate-400 text-sm">
                                                No records found.
                                            </td>
                                        </tr>
                                    ) : (
                                        tableRows.map((row, i) => {
                                            if (row._isSeparator) {
                                                const style = DAY_LABEL_STYLES[row._label] || DAY_LABEL_STYLES["Older"];
                                                return (
                                                    <tr key={`sep-${row._label}`}>
                                                        <td colSpan={20}
                                                            className={`px-4 py-2 ${style.separator}`}>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                                                                <span className="text-xs font-bold uppercase tracking-widest">
                                                                    {row._label}
                                                                </span>
                                                                <span className={`ml-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${style.pill}`}>
                                                                    {row._count} record{row._count !== 1 ? "s" : ""}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            const dur = calcDuration(row.check_in, row.check_out);
                                            const labelStyle = DAY_LABEL_STYLES[row._dayLabel] || DAY_LABEL_STYLES["Older"];

                                            return (
                                                <tr key={row.slNo || i}
                                                    className="border-t border-slate-100 transition-colors hover:bg-blue-50/60">
                                                    <td className="p-3 text-slate-400 text-xs">{i}</td>
                                                    <td className="p-3 text-slate-800 text-xs font-semibold whitespace-nowrap">
                                                        {row.employee_name || "-"}
                                                    </td>
                                                    <td className="p-3 text-xs whitespace-nowrap">
                                                        <div className="flex flex-col gap-0.5">
                                                            <span className="text-slate-700 font-medium">{row.routine_date}</span>
                                                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full w-fit ${labelStyle.pill}`}>
                                                                {row._dayLabel}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        {row.visit_type && <Badge text={row.visit_type} />}
                                                    </td>
                                                    <td className="p-3 text-slate-600 text-xs">{row.client_type || "-"}</td>
                                                    <td className="p-3 text-slate-600 text-xs max-w-[120px] truncate">{row.visit_purpose || "-"}</td>
                                                    <td className="p-3 text-slate-600 text-xs">{row.product || "-"}</td>
                                                    <td className="p-3 text-slate-700 text-xs font-medium">{row.customer_name || "-"}</td>
                                                    <td className="p-3 text-slate-500 text-xs max-w-[120px] truncate">{row.address || "-"}</td>
                                                    <td className="p-3 text-slate-600 text-xs">{row.contact_person || "-"}</td>
                                                    <td className="p-3 text-slate-500 text-xs">{row.contact_number || "-"}</td>
                                                    <td className="p-3 text-slate-500 text-xs max-w-[120px] truncate">{row.location_name || row.gps_location || "-"}</td>
                                                    <td className="p-3 text-slate-500 text-xs">{row.tally_serial_number || "-"}</td>
                                                    <td className="p-3 text-center">
                                                        {dur ? (
                                                            <span className="bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 rounded-full text-xs font-semibold">
                                                                {dur}
                                                            </span>
                                                        ) : <span className="text-slate-300">—</span>}
                                                    </td>
                                                    <td className="p-3 text-slate-500 text-xs max-w-[150px] truncate">{row.remarks || "-"}</td>
                                                    <td className="p-3 text-slate-500 text-xs">{row.referred_by_contact_person || "-"}</td>
                                                    <td className="p-3 text-slate-500 text-xs">{row.referred_person_number || "-"}</td>
                                                    <td className="p-3 text-slate-600 text-xs whitespace-nowrap">
                                                        {row.travel_from && row.travel_to
                                                            ? <span>{row.travel_from} <span className="text-slate-400">→</span> {row.travel_to}</span>
                                                            : <span className="text-slate-300">—</span>}
                                                    </td>
                                                    <td className="p-3 text-slate-500 text-xs">{row.mode_of_transpotation || "-"}</td>
                                                    <td className="p-3 text-right">
                                                        {row.expense_amount ? (
                                                            <span className="text-emerald-700 font-bold text-xs">₹{row.expense_amount}</span>
                                                        ) : <span className="text-slate-300">—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}